/**
 * @fileoverview Pure helpers for the "slot" model of variant blocks.
 *
 * A variant group is a grid of slots (rows) × options (columns). A slot is an
 * explicit label on each snippet (`slotLabel`): snippets sharing
 * (groupSlug, slotLabel) are the same row, one cell per option — e.g. a
 * "Greeting" slot whose cells are Greeting-Mum / Greeting-Dad. The precedent
 * editor picks a slot by label; the option is chosen later at job time.
 *
 * Legacy snippets without a slotLabel are paired by POSITION (the i-th snippet
 * of each option, in input/creation order) so the feature still works before
 * the one-off backfill runs.
 */

export interface SlotSnippet {
  _id: string;
  groupSlug: string;
  optionValue: string;
  name: string;
  slotLabel?: string | null;
}

export interface Slot {
  /** Stable key for the slot — the label, or `slot_<i>` for position pairs. */
  key: string;
  /** Display label shown in the slot picker. */
  label: string;
  /** option value → snippet _id, for options that have a cell in this slot. */
  refs: Record<string, string>;
}

/**
 * Build the slots (rows) for a group. Prefers explicit `slotLabel` grouping;
 * falls back to position pairing when no snippet in the group has a label yet.
 */
export function buildSlots(
  snippets: SlotSnippet[],
  groupSlug: string,
  optionValues: string[],
): Slot[] {
  const groupSnips = snippets.filter((s) => s.groupSlug === groupSlug);
  if (groupSnips.length === 0) return [];

  const anyLabeled = groupSnips.some((s) => !!s.slotLabel);

  if (anyLabeled) {
    // Group by slotLabel, preserving first-seen order. Snippets with no label
    // become their own singleton slot keyed by name (rare, transient state).
    const order: string[] = [];
    const byLabel = new Map<string, Slot>();
    for (const s of groupSnips) {
      const label = s.slotLabel || s.name;
      const slot =
        byLabel.get(label) ??
        (() => {
          const created: Slot = { key: label, label, refs: {} };
          byLabel.set(label, created);
          order.push(label);
          return created;
        })();
      // First cell wins for a given (slot, option).
      if (!(s.optionValue in slot.refs)) slot.refs[s.optionValue] = s._id;
    }
    return order.map((l) => byLabel.get(l) as Slot);
  }

  // Position pairing: i-th snippet of each option, in input order.
  const byOption = new Map<string, SlotSnippet[]>();
  for (const opt of optionValues) byOption.set(opt, []);
  for (const s of groupSnips) {
    const bucket = byOption.get(s.optionValue);
    if (bucket) bucket.push(s);
  }
  const maxLen = Math.max(
    0,
    ...optionValues.map((o) => byOption.get(o)?.length ?? 0),
  );
  const slots: Slot[] = [];
  for (let i = 0; i < maxLen; i++) {
    const refs: Record<string, string> = {};
    for (const opt of optionValues) {
      const s = byOption.get(opt)?.[i];
      if (s) refs[opt] = s._id;
    }
    const label = optionValues
      .map((opt) => byOption.get(opt)?.[i])
      .find((s) => s)?.name;
    slots.push({ key: `slot_${i}`, label: label ?? `Variant ${i + 1}`, refs });
  }
  return slots;
}

/** Option values that have no cell in the given slot. */
export function missingOptionsForSlot(
  slot: Slot,
  optionValues: string[],
): string[] {
  return optionValues.filter((o) => !(o in slot.refs));
}

/**
 * The slot label a set of snippet refs resolves to (for labelling a block that
 * already references snippets). Returns the slotLabel of the first referenced
 * snippet, falling back to its name.
 */
export function slotLabelForRefs(
  snippets: SlotSnippet[],
  refs: Record<string, string | null> | null | undefined,
): string | undefined {
  if (!refs) return undefined;
  for (const id of Object.values(refs)) {
    if (!id) continue;
    const snip = snippets.find((s) => s._id === id);
    if (snip) return snip.slotLabel || snip.name;
  }
  return undefined;
}
